const requireEnvVar = (key: keyof ImportMetaEnv): string => {
  const value = import.meta.env[key];
  if (value == null || value.trim() === '') {
    throw new Error(`Missing required environment variable: ${key}`);
  }
  return value;
};

const parseNumber = (value: string, key: string): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    throw new Error(`Invalid numeric value for ${key}: ${value}`);
  }
  return parsed;
};

const parseBoolean = (value: string, key: string): boolean => {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'true') return true;
  if (normalized === 'false') return false;
  throw new Error(`Invalid boolean value for ${key}: ${value}`);
};

const parseList = (value: string): string[] => {
  const parsed = value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

  if (parsed.length === 0) {
    throw new Error('VITE_ALLOWED_FILE_TYPES must define at least one value');
  }

  return parsed;
};

const runtimeEnvVar = (key: keyof NonNullable<Window["__SSIGNER_CONFIG__"]>): string | undefined => {
  const value = window.__SSIGNER_CONFIG__?.[key]?.trim();
  if (!value || value.charCodeAt(0) === 36) return undefined;
  return value;
};

const requireApiUrl = (): string => {
  const value = runtimeEnvVar('apiUrl') ?? import.meta.env.VITE_API_URL?.trim();
  if (!value) {
    throw new Error('Missing required runtime environment variable: VITE_API_URL');
  }
  return value;
};

export const env = Object.freeze({
  apiUrl: requireApiUrl(),
  apiTimeoutMs: parseNumber(requireEnvVar('VITE_API_TIMEOUT'), 'VITE_API_TIMEOUT'),
  appName: requireEnvVar('VITE_APP_NAME'),
  appVersion: requireEnvVar('VITE_VERSION'),
  environment: requireEnvVar('VITE_ENVIRONMENT'),
  enableDebug: parseBoolean(requireEnvVar('VITE_ENABLE_DEBUG'), 'VITE_ENABLE_DEBUG'),
  enableAnalytics: parseBoolean(requireEnvVar('VITE_ENABLE_ANALYTICS'), 'VITE_ENABLE_ANALYTICS'),
  enableDarkMode: parseBoolean(requireEnvVar('VITE_ENABLE_DARK_MODE'), 'VITE_ENABLE_DARK_MODE'),
  maxFileSizeBytes: parseNumber(requireEnvVar('VITE_MAX_FILE_SIZE'), 'VITE_MAX_FILE_SIZE'),
  allowedFileTypes: parseList(requireEnvVar('VITE_ALLOWED_FILE_TYPES')),
  tokenStorageKey: requireEnvVar('VITE_TOKEN_STORAGE_KEY'),
  refreshTokenStorageKey: requireEnvVar('VITE_REFRESH_TOKEN_STORAGE_KEY'),
  idleTimeoutMinutes: parseNumber(requireEnvVar('VITE_IDLE_TIMEOUT_MINUTES'), 'VITE_IDLE_TIMEOUT_MINUTES'),
  idleWarningSeconds: parseNumber(requireEnvVar('VITE_IDLE_WARNING_SECONDS'), 'VITE_IDLE_WARNING_SECONDS'),
  absoluteTimeoutHours: parseNumber(requireEnvVar('VITE_ABSOLUTE_TIMEOUT_HOURS'), 'VITE_ABSOLUTE_TIMEOUT_HOURS'),
  sessionTimeoutMs: parseNumber(requireEnvVar('VITE_SESSION_TIMEOUT'), 'VITE_SESSION_TIMEOUT'),
  mockApi: parseBoolean(requireEnvVar('VITE_MOCK_API'), 'VITE_MOCK_API'),
  logLevel: requireEnvVar('VITE_LOG_LEVEL'),
  publicUrl: requireEnvVar('VITE_PUBLIC_URL'),
  forceHttps: parseBoolean(requireEnvVar('VITE_FORCE_HTTPS'), 'VITE_FORCE_HTTPS'),
  generateSourceMap: parseBoolean(requireEnvVar('VITE_GENERATE_SOURCEMAP'), 'VITE_GENERATE_SOURCEMAP'),
  enableCompression: parseBoolean(requireEnvVar('VITE_ENABLE_COMPRESSION'), 'VITE_ENABLE_COMPRESSION'),
  signatureComponentDownloadUrl: requireEnvVar('VITE_SIGNATURE_COMPONENT_DOWNLOAD_URL'),
  ssignerBaseURL: requireEnvVar('VITE_SSIGNER_BASE_URL'),
  ssignerDebug: requireEnvVar('VITE_SSIGNER_DEBUG'),
});
